import { isApiError, problemDetailText } from '../../services/apiError'
import { NOT_MEASURED, errorKind, errorSummary, whenLabel } from '../../utils/format'
import type { AcknowledgeEtlHaltResponse, EtlStorageCapacity, EtlStorageFile } from '../../types/etlHalts'
import type { EtlJobCatalog } from '../../types/etlJobs'

/**
 * The Scheduler's clear-halt control, as pure decisions: whether to offer it, what the capacity readout says,
 * what the toast says, and which error sentence to show.
 *
 * Pure on purpose — no React, no MSAL. `src/auth` reads `window` at module load, so whether this build has an
 * Entra sign-in arrives as an argument (JobsPage passes `isEntraConfigured()`), never as an import.
 *
 * TWO RULES run through it. A write FAILS CLOSED: the control is offered only when the server says
 * `canClearHalts: true`, never on a missing answer. And an unobserved thing is never green: a capacity readout
 * with nothing measured is a dash, never "healthy".
 */

/** The server's own column bound (EtlRun.HaltDetail, nvarchar(1024)); the API refuses a longer note with a 400. */
export const NOTE_MAX_LENGTH = 1024

/** What clearing does, and what it does not. */
export const CLEAR_CONSEQUENCES =
  'Clearing lets refused Warehouse, Entity and Maintenance jobs run again; it frees no space.'

/** Under the capacity readout: what it was measured against, and that it is not the worker's check. */
export const CAPACITY_CAPTION =
  "Checked with no projected load against the API's configured reserve; the worker re-checks before every chunk "
  + 'against its own (see /configuration).'

/** When the catalogue has not answered whether this session may clear. */
export const CLEAR_UNAVAILABLE_TEXT =
  'Clearing a halt is not offered: the job catalogue has not said whether this session may clear one. An '
  + 'administrator clears it here or through the API (POST /api/etl/halts/{runId}/acknowledge), with an Entra '
  + 'Bearer sign-in holding Lighthouse.Admin, and a note.'

/** The remedy when this build cannot sign anyone in with Entra at all. */
export const NO_ENTRA_REMEDY = 'This build has no Entra sign-in.'

/** When the server refused without saying why. */
export const CLEAR_REFUSED_FALLBACK = 'The server says this session may not clear a halt.'

/** The second half of the sign-in sentence both ETL writes use. */
export const ENTRA_ONLY =
  'this action accepts only an Entra sign-in; the shared API key is refused even locally. Sign in again.'

/**
 * The same sentence for a build with no Entra sign-in, where "Sign in again" is impossible: nothing in this build
 * can sign anyone in with Entra, so no session of it can perform either write.
 */
export const ENTRA_ONLY_NO_ENTRA =
  'this action accepts only an Entra sign-in, and this build has none; the shared API key is refused even locally, '
  + 'so no session of this build can do it. Configure its Entra sign-in (the VITE_ENTRA_* settings) to act from here.'

/**
 * Appended to the server's Run refusal in a build with no Entra sign-in, which the server cannot know. It names the
 * only other door truthfully: the Hangfire dashboard starts a job only for a Lighthouse.Admin holder signed in to
 * the dashboard's own Entra registration, and is read-only without one (Automaton.Dashboard Program.cs, IsReadOnlyFunc).
 */
export const NO_ENTRA_RUN_REMEDY =
  'This build has no Entra sign-in, so no session of it can run a job. The Hangfire dashboard can start one only '
  + 'when it has an Entra sign-in of its own and you hold Lighthouse.Admin there; without one it is read-only.'

// ---- the gate ---------------------------------------------------------------------------------------------

export type ClearHaltGate =
  | { kind: 'offer' }
  | { kind: 'refused'; reason: string; remedy: string | null }
  | { kind: 'unavailable' }

/**
 * Whether to offer "Clear this halt…".
 *
 * - `offer` ONLY when the server says `canClearHalts: true`.
 * - `refused` when it says false: its reason, plus "This build has no Entra sign-in." when that is why no
 *   session here could ever clear one.
 * - `unavailable` when the catalogue, its permission, or the field is missing (loading, failed, an older API).
 *   A write fails closed: never offered on an answer nobody gave.
 */
export function clearHaltGate(catalog: EtlJobCatalog | undefined, entraConfigured: boolean): ClearHaltGate {
  const permission = catalog?.permission
  if (!permission) return { kind: 'unavailable' }
  if (permission.canClearHalts === true) return { kind: 'offer' }
  if (permission.canClearHalts === false) {
    return {
      kind: 'refused',
      reason: permission.deniedReason?.trim() || CLEAR_REFUSED_FALLBACK,
      remedy: entraConfigured ? null : NO_ENTRA_REMEDY,
    }
  }
  return { kind: 'unavailable' }
}

// ---- the capacity readout ---------------------------------------------------------------------------------

/** The readout's query, as much of it as the view needs. */
export interface CapacityQuery {
  data?: EtlStorageCapacity
  isError: boolean
  error: unknown
}

export type CapacityView =
  | { kind: 'loading'; text: string }
  | { kind: 'restricted' }
  | { kind: 'failed'; text: string; summary: string | null }
  | { kind: 'intact'; text: string; detail: string; files: EtlStorageFile[] }
  | { kind: 'short'; text: string; detail: string; files: EtlStorageFile[] }
  | { kind: 'unmeasured'; text: string; detail: string }

export type CapacityKind = CapacityView['kind']

/**
 * What the capacity readout shows. Loading, restricted, failed, intact, short and unmeasured all look different,
 * and only a served `isSufficient: true` reads as intact — `null` is NOT_MEASURED, never healthy.
 */
export function capacityView(query: CapacityQuery): CapacityView {
  if (query.isError) {
    if (errorKind(query.error) === 'forbidden') return { kind: 'restricted' }
    return {
      kind: 'failed',
      text: 'Could not read storage headroom — this says nothing about whether there is room',
      summary: errorSummary(query.error),
    }
  }

  const data = query.data
  if (!data) return { kind: 'loading', text: 'Reading storage headroom…' }

  if (data.isSufficient === true) {
    return { kind: 'intact', text: 'Reserve intact now', detail: data.detail, files: data.files ?? [] }
  }
  if (data.isSufficient === false) {
    return { kind: 'short', text: 'Reserve short now', detail: data.detail, files: data.files ?? [] }
  }
  return { kind: 'unmeasured', text: NOT_MEASURED, detail: data.detail }
}

// ---- the toast --------------------------------------------------------------------------------------------

export interface ClearToast {
  title: string
  message: string
  color: string
  /** False keeps it on screen until dismissed: a warning that the next load will likely halt again. */
  autoClose?: false
}

/**
 * What to say once the server answered.
 *
 * A repeat clear is not a success: someone else cleared it first, and this note was NOT recorded. A clear into a
 * reserve still short stays on screen. A clear with nothing measured says so rather than sounding healthy. None
 * of them claims a signal resolved: the detector decides that on its own schedule.
 */
export function clearToast(response: AcknowledgeEtlHaltResponse): ClearToast {
  const halt = response.halt
  const detail = response.capacityDetail?.trim()

  if (!response.clearedByThisRequest) {
    return {
      title: 'Already cleared',
      message: `Already cleared by ${halt.acknowledgedBy ?? NOT_MEASURED} at ${whenLabel(halt.acknowledgedAt)}; `
        + 'your note was not recorded.',
      color: 'yellow',
    }
  }

  if (response.capacityLooksHealthy === true) {
    return {
      title: 'Halt cleared',
      message: `Run ${halt.runKey} no longer blocks runs. The reserve looks intact now.`,
      color: 'blue',
    }
  }

  if (response.capacityLooksHealthy === false) {
    return {
      title: 'Halt cleared — reserve still short',
      message: `Run ${halt.runKey} was cleared but the reserve is still short; the next load will likely halt again.`
        + (detail ? ` ${detail}` : ''),
      color: 'orange',
      autoClose: false,
    }
  }

  return {
    title: 'Halt cleared',
    message: `Run ${halt.runKey} was cleared, but headroom could not be measured, so this says nothing about whether `
      + 'there is room now.' + (detail ? ` ${detail}` : ''),
    color: 'yellow',
  }
}

// ---- the remaining halts ----------------------------------------------------------------------------------

/**
 * What still blocks runs once this halt is cleared — counted from the list the button came from (the polled halt
 * list), never from the catalogue's `activeHaltCount`, which is up to a minute stale.
 */
export function remainingHaltsText(active: readonly { runId: string }[], runId: string): string {
  const others = active.filter((h) => h.runId !== runId).length
  if (others === 0) return 'No other halt is blocking: once this one is cleared, no recorded halt blocks runs.'
  return `${others} other halt${others === 1 ? '' : 's'} will still block runs after this one is cleared.`
}

// ---- errors -----------------------------------------------------------------------------------------------

/**
 * The sign-in sentence for either ETL write, or null when the error is something else. `sign-in-required` was never
 * sent (no Entra token here); a 401 was sent and refused. `entraConfigured` is whether this build can sign anyone in
 * with Entra at all (the caller passes `isEntraConfigured()`); without it "Sign in again" would be advice nobody can
 * follow, so the sentence says the build has none instead.
 */
export function signInRequiredText(error: unknown, entraConfigured = true): string | null {
  const kind = errorKind(error)
  const tail = entraConfigured ? ENTRA_ONLY : ENTRA_ONLY_NO_ENTRA
  if (kind === 'sign-in-required') return `Not sent: ${tail}`
  if (kind === 'unauthorized') return `Refused: ${tail}`
  return null
}

/** What to add to the server's Run refusal: nothing when this build has Entra, else {@link NO_ENTRA_RUN_REMEDY}. */
export function runDeniedRemedy(entraConfigured: boolean): string | null {
  return entraConfigured ? null : NO_ENTRA_RUN_REMEDY
}

/**
 * Why a clear failed, for the dialog — which stays open with the note intact.
 *
 * - 400: the server's own words (no note; longer than the column).
 * - 401 / not sent: sign in with Entra.
 * - 403: the server's problem detail first — the identity refusal names it — else the missing role.
 * - 404: no such halt.
 * - 5xx / network: it is not known whether the clear was recorded, and retrying is safe.
 */
export function clearErrorText(error: unknown, entraConfigured = true): string {
  const signIn = signInRequiredText(error, entraConfigured)
  if (signIn) return signIn

  const kind = errorKind(error)
  const detail = isApiError(error) ? problemDetailText(error.body) : null

  switch (kind) {
    case 'bad-request':
      return detail ?? errorSummary(error) ?? 'The server refused the note.'
    case 'forbidden':
      return detail ?? 'Clearing a halt requires the Lighthouse.Admin app role, which this account does not hold.'
    case 'not-found':
      return 'No such halt: the run either does not exist or did not halt.'
    case 'server':
    case 'network':
      return 'Could not clear the halt: unknown whether it was recorded — retrying is safe; a repeat clear changes '
        + 'nothing.'
    default:
      return errorSummary(error) ?? 'Could not clear the halt.'
  }
}

// ---- the confirm button -----------------------------------------------------------------------------------

/** A note is required; a short reserve needs the "I know the reserve is still short" box ticked as well. */
export function confirmEnabled(note: string, capacityKind: CapacityKind, ackShort: boolean): boolean {
  return note.trim() !== '' && (capacityKind !== 'short' || ackShort)
}

/** Where the audit trail shows this run's clear (and its full note). */
export function haltAuditLink(runId: string): string {
  const params = new URLSearchParams({ category: 'etl-operations', entityId: runId })
  return `/audit?${params.toString()}`
}
